import React, { useState, useEffect } from "react";
import { getUserProfile, updateUserProfile } from "../services/api";
import { FaPencilAlt, FaSave } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import './Profile.css';

const Profile = () => {
    const [user, setUser] = useState({
        name: "",
        email: "",
        phone: "",
        address: { street: "", city: "", state: "", country: "" },
        hobbies: [],
        bio: "",
        image: "",
        role: "",
        createdAt: "",
    });
    const [loading, setLoading] = useState(true);
    const [newImage, setNewImage] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await getUserProfile();
                const profileData = response.user || {};
                const address = profileData.address || { street: "", city: "", state: "", country: "" };
                const hobbies = Array.isArray(profileData.hobbies) ? profileData.hobbies : [];

                setUser({
                    ...profileData,
                    address,
                    hobbies,
                });
                setLoading(false);
            } catch (error) {
                console.error("Error fetching profile", error);
                setLoading(false);
                if (error.message === 'Invalid token') {
                    alert('Session expired. Please login again.');
                    navigate("/login");
                }
            }
        };

        fetchProfile();
    }, [navigate]);

    const handleProfileUpdate = async () => {
        const formData = new FormData();
        formData.append("name", user.name);
        formData.append("email", user.email);
        formData.append("phone", user.phone);
        formData.append("bio", user.bio);
        formData.append("address", JSON.stringify(user.address));
        formData.append("hobbies", JSON.stringify(user.hobbies));

        if (newImage) formData.append("image", newImage);

        try {
            const response = await updateUserProfile(formData);
            setUser(response.user);
            alert("Profile updated successfully!");
        } catch (error) {
            console.error("Error updating profile", error);
            alert("Error occurred during profile update");
        }
    };

    const handleFileChange = (e) => {
        setNewImage(e.target.files[0]);
    };

    const handleFieldChange = (field, value) => {
        setUser((prevUser) => {
            if (field === "hobbies") {
                return {
                    ...prevUser,
                    [field]: value.split(",").map((hobby) => hobby.trim()),
                };
            } else if (field.includes("address.")) {
                const addressField = field.split(".")[1];
                return {
                    ...prevUser,
                    address: {
                        ...prevUser.address,
                        [addressField]: value,
                    },
                };
            } else {
                return {
                    ...prevUser,
                    [field]: value,
                };
            }
        });
    };

    const handleSave = () => {
        setEditingField(null);
        handleProfileUpdate();
    };

    const handleLogout = () => {
        const confirmLogout = window.confirm("Are you sure you want to log out?");
        if (confirmLogout) {
            localStorage.removeItem("authToken");
            navigate("/login");
        }
    };

    useEffect(() => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            navigate("/login");
        }
    }, [navigate]);

    const renderField = (field, value, type = "text") => {
        return editingField === field ? (
            <>
                <input
                    type={type}
                    className="form-control d-inline-block w-75"
                    value={value}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                />
                <button
                    className="btn btn-success btn-sm ms-2"
                    onClick={handleSave}
                >
                    <FaSave />
                </button>
            </>
        ) : (
            <>
                <span>{value}</span>
                <button
                    className="btn btn-outline-primary btn-sm ms-2"
                    onClick={() => setEditingField(field)}
                >
                    <FaPencilAlt />
                </button>
            </>
        );
    };

    if (loading) {
        return <div className="text-center">Loading...</div>;
    }

    const imageUrl = user.image ? `http://localhost:5000/${user.image}` : "default-avatar.jpg";

    return (
        <div className="container profile-container">
            <div className="row justify-content-center">
                {/* Profile Card (Image + Logout + Account Info) */}
                <div className="col-md-4">
                    <div className="card shadow-sm mb-4">
                        <div className="card-body text-center">
                            <img
                                src={imageUrl}
                                alt="User"
                                className="img-fluid rounded-circle mb-3 profile-image"
                                style={{ width: 150, height: 150 }}
                            />
                            <input
                                type="file"
                                accept="image/*"
                                className="mb-3"
                                onChange={handleFileChange}
                            />
                            <button className="btn btn-primary mb-3" onClick={handleProfileUpdate}>
                                Update Profile Image
                            </button>
                            <h5>{user.name}</h5>
                            <p>{user.email}</p>
                            <button className="btn btn-danger" onClick={handleLogout}>
                                Logout
                            </button>
                        </div>
                    </div>
                </div>

                {/* Personal Details Card */}
                <div className="col-md-4">
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h5 className="card-title">Personal Details</h5>
                            <p>{renderField("phone", user.phone)}</p>
                            <p>{renderField("bio", user.bio)}</p>
                        </div>
                    </div>
                </div>

                {/* Address Card */}
                <div className="col-md-4">
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h5 className="card-title">Address</h5>
                            <div className="row">
                                <div className="col">
                                    {renderField("address.street", user.address.street)}
                                </div>
                                <div className="col">
                                    {renderField("address.city", user.address.city)}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col">
                                    {renderField("address.state", user.address.state)}
                                </div>
                                <div className="col">
                                    {renderField("address.country", user.address.country)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Hobbies & Bio Card */}
                <div className="col-md-4">
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h5 className="card-title">Hobbies</h5>
                            {renderField("hobbies", user.hobbies.join(", "), "text")}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
